
import React, { useState } from 'react';
import pkg from '../../package.json';
import type { LoginCredentials } from '../../types';
import { Icons } from '../ui/Icons';

interface LoginProps {
    onLogin: (credentials: Omit<LoginCredentials, 'name'>, rememberMe: boolean) => void;
    onForgotPassword: (email: string) => void;
    onRegisterFirstAdmin?: (credentials: LoginCredentials) => void;
    adminCount: number;
    propertyName: string;
}

const Login: React.FC<LoginProps> = ({ onLogin, onForgotPassword, onRegisterFirstAdmin, adminCount, propertyName }) => {
    const [isRegistering, setIsRegistering] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(true);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (isRegistering && onRegisterFirstAdmin) {
            onRegisterFirstAdmin({ name, email, password });
        } else {
            onLogin({ email, password }, rememberMe);
        }
    };

    const handleForgotPasswordClick = (e: React.MouseEvent) => {
        e.preventDefault();
        onForgotPassword(email);
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center p-6 sm:p-12">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                     <Icons.Calendar className="h-10 w-10 mx-auto text-indigo-600"/>
                    <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Scheduler Pro</h1>
                    <p className="mt-1 text-lg font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">{propertyName}</p>
                </div>
                
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 text-center">
                    {isRegistering ? 'Register First Admin' : 'Sign In'}
                </h2>
                 <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                    {isRegistering 
                        ? 'No administrators found. Create the first account to get started.' 
                        : 'Welcome back! Please enter your details.'}
                </p>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        {isRegistering && (
                            <div>
                                <label htmlFor="full-name" className="sr-only">Full Name</label>
                                <div className="relative">
                                     <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                        <Icons.User className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        id="full-name"
                                        name="name"
                                        type="text"
                                        required
                                        className="block w-full rounded-md border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-200 pl-10 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-2.5"
                                        placeholder="Full Name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </div>
                            </div>
                        )}
                        <div>
                            <label htmlFor="email-address" className="sr-only">Email address</label>
                            <div className="relative">
                                 <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <Icons.Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="email-address"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    className="block w-full rounded-md border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-200 pl-10 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-2.5"
                                    placeholder="Email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="password" className="sr-only">Password</label>
                             <div className="relative">
                                 <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <Icons.Lock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    className="block w-full rounded-md border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-200 pl-10 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-2.5"
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {!isRegistering && (
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    name="remember-me"
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-indigo-600 focus:ring-indigo-500"
                                />
                                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                                    Remember me
                                </label>
                            </div>

                            <div className="text-sm">
                                <a href="#" onClick={handleForgotPasswordClick} className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300">
                                    Forgot password?
                                </a>
                            </div>
                        </div>
                    )}

                    <div>
                        <button
                            type="submit"
                            className="group relative flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2.5 px-4 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
                        >
                            {isRegistering ? 'Create Admin Account' : 'Sign In'}
                        </button>
                    </div>
                </form>
                
                {adminCount === 0 && !isRegistering && (
                    <div className="mt-4">
                        <button
                            onClick={() => setIsRegistering(true)}
                            className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Setup First Administrator
                        </button>
                    </div>
                )}

                {isRegistering && (
                    <div className="mt-4">
                        <button
                            onClick={() => setIsRegistering(false)}
                            className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Back to Login
                        </button>
                    </div>
                )}
                <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    <p>Staff members should use their assigned credentials.</p>
                    <p className="mt-4 text-xs opacity-50">v{pkg.version}</p>
                </div>
            </div>
        </div>
    );
};

export default Login;